
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { toast } from "sonner";

export function useEmailTemplatesList() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailTemplateId, setTestEmailTemplateId] = useState<string | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5); // Reduced from 10 to improve performance
  const [hasError, setHasError] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Use the optimized hook with pagination and reduced page size
  const { 
    loading, 
    templates, 
    fetchTemplates, 
    sendTestEmail,
    loadingAction,
    error,
    pagination
  } = useEmailTemplates(currentPage, pageSize);
  
  // Memoized filter function to avoid excessive filtering
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Filter by search query
      const matchesSearch = !searchQuery || 
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.template_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
  
      // Filter by category
      const matchesCategory = activeTab === 'all' || template.category === activeTab;
      
      return matchesSearch && matchesCategory && template.is_active;
    });
  }, [templates, searchQuery, activeTab]);
  
  // Memoize category options to avoid recalculations
  const categoryOptions = useMemo(() => {
    return ['all', ...Array.from(new Set(templates.map(t => t.category)))].filter(Boolean);
  }, [templates]);
  
  // Stable callback for data loading
  const loadData = useCallback(async () => {
    if (!isInitialLoad && !hasError) return;
    
    setIsLoading(true);
    setHasError(false);
    
    try {
      console.log("Fetching email templates...");
      await fetchTemplates();
      setIsInitialLoad(false);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setHasError(true);
      toast.error("Could not load email templates");
    } finally {
      // Small delay to ensure smooth transition
      setTimeout(() => setIsLoading(false), 100);
    }
  }, [fetchTemplates, isInitialLoad, hasError]);
  
  // Load data when component mounts or page changes
  useEffect(() => {
    loadData();
  }, [loadData, currentPage]);
  
  const handleOpenTestEmailModal = useCallback((templateId: string) => {
    setTestEmailTemplateId(templateId);
    setIsTestEmailModalOpen(true);
  }, []);
  
  const handleSendTestEmail = async () => {
    if (!testEmailTemplateId || !testEmailAddress) return;
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailAddress)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    const success = await sendTestEmail(testEmailTemplateId, testEmailAddress);
    if (success) {
      setIsTestEmailModalOpen(false);
      setTestEmailAddress('');
      setTestEmailTemplateId(null);
    }
  };

  const handleTemplateCreated = () => {
    fetchTemplates();
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setActiveTab('all');
  };

  const handleRetry = async () => {
    setHasError(false);
    setIsLoading(true);
    setIsInitialLoad(true);
    try {
      await fetchTemplates();
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  };
  
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      setIsLoading(true); // Show loading state when changing pages
    }
  };

  const handleOpenPreview = useCallback((templateId: string) => {
    setPreviewTemplateId(templateId);
    setIsPreviewDialogOpen(true);
  }, []);

  const handleCreateTemplate = () => {
    setIsCreateTemplateOpen(true);
  };

  return {
    activeTab,
    searchQuery,
    isTestEmailModalOpen,
    setIsTestEmailModalOpen,
    testEmailAddress,
    setTestEmailAddress,
    testEmailTemplateId,
    isPreviewDialogOpen,
    setIsPreviewDialogOpen,
    previewTemplateId,
    isCreateTemplateOpen,
    setIsCreateTemplateOpen,
    isLoading,
    hasError,
    loading,
    templates,
    filteredTemplates,
    categoryOptions,
    currentPage,
    pagination,
    loadingAction,
    handleOpenTestEmailModal,
    handleSendTestEmail,
    handleTemplateCreated,
    handleSearchChange,
    handleTabChange,
    handleResetFilters,
    handleRetry,
    handlePageChange,
    handleOpenPreview,
    handleCreateTemplate
  };
}
